import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaUser, FaMusic, FaCompactDisc, FaHeadphones, FaHeart, FaListUl, FaEdit, FaSave, FaTimes, FaGlobe, FaLock } from 'react-icons/fa';
import axios from 'axios';
import '../styles/ProfilePage.css';

const API = process.env.REACT_APP_API_URL || '';

function ProfilePage() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [isOwn, setIsOwn] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProfile();
  }, [username]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      if (!username) {
        const res = await axios.get(`${API}/api/profiles/me`);
        setProfile(res.data);
        setForm(res.data);
        setIsOwn(true);
      } else {
        const res = await axios.get(`${API}/api/profiles/${username}`);
        setProfile(res.data);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Profil non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      await axios.put(`${API}/api/profiles/me`, form);
      setProfile({ ...profile, ...form });
      setEditing(false);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="profile-loading">Chargement...</div>;
  if (error) return <div className="profile-error">{error}</div>;
  if (!profile) return null;

  return (
    <div className="profile-page">
      <div className="profile-header-card">
        <div className="profile-avatar">
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" />
          ) : (
            <FaUser />
          )}
        </div>
        <div className="profile-header-info">
          {editing ? (
            <div className="profile-edit-form">
              <input value={form.display_name || ''} onChange={e => setForm({...form, display_name: e.target.value})} placeholder="Nom affiché" />
              <textarea value={form.bio || ''} onChange={e => setForm({...form, bio: e.target.value})} placeholder="Bio..." rows={3} />
              <input value={form.favorite_genre || ''} onChange={e => setForm({...form, favorite_genre: e.target.value})} placeholder="Genre favori" />
              <label className="profile-public-toggle">
                <input type="checkbox" checked={form.profile_public || false} onChange={e => setForm({...form, profile_public: e.target.checked})} />
                {form.profile_public ? <><FaGlobe /> Profil public</> : <><FaLock /> Profil privé</>}
              </label>
              <div className="profile-edit-actions">
                <button onClick={saveProfile} className="save-btn"><FaSave /> Sauvegarder</button>
                <button onClick={() => { setEditing(false); setForm(profile); }} className="cancel-btn"><FaTimes /> Annuler</button>
              </div>
            </div>
          ) : (
            <>
              <h1>{profile.display_name || profile.username}</h1>
              <p className="profile-username">@{profile.username}</p>
              {profile.bio && <p className="profile-bio">{profile.bio}</p>}
              {profile.favorite_genre && <span className="profile-genre">🎵 {profile.favorite_genre}</span>}
              <p className="profile-joined">Membre depuis {new Date(profile.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</p>
              {isOwn && <button onClick={() => setEditing(true)} className="edit-profile-btn"><FaEdit /> Modifier</button>}
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      {profile.stats && (
        <div className="profile-stats-row">
          <div className="profile-stat"><FaHeadphones /><span>{profile.stats.total_listens}</span><small>écoutes</small></div>
          <div className="profile-stat"><FaMusic /><span>{profile.stats.unique_tracks}</span><small>pistes</small></div>
          <div className="profile-stat"><FaCompactDisc /><span>{profile.stats.unique_albums}</span><small>albums</small></div>
        </div>
      )}

      {/* Top Albums */}
      {profile.topAlbums?.length > 0 && (
        <div className="profile-section">
          <h2><FaCompactDisc /> Top Albums</h2>
          <div className="profile-albums-grid">
            {profile.topAlbums.map(a => (
              <div key={a.id} className="profile-album-card">
                <img src={a.cover_url ? (a.cover_url.startsWith('http') ? a.cover_url : `${API}${a.cover_url}`) : '/logo192.png'} alt="" />
                <div className="profile-album-title">{a.title}</div>
                <div className="profile-album-plays">{a.plays} écoutes</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Favoris */}
      {profile.favorites?.length > 0 && (
        <div className="profile-section">
          <h2><FaHeart /> Favoris</h2>
          <div className="profile-albums-grid">
            {profile.favorites.map(a => (
              <div key={a.id} className="profile-album-card">
                <img src={a.cover_url ? (a.cover_url.startsWith('http') ? a.cover_url : `${API}${a.cover_url}`) : '/logo192.png'} alt="" />
                <div className="profile-album-title">{a.title}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Playlists publiques */}
      {profile.playlists?.length > 0 && (
        <div className="profile-section">
          <h2><FaListUl /> Playlists publiques</h2>
          <div className="profile-playlists">
            {profile.playlists.map(p => (
              <div key={p.id} className="profile-playlist-item">
                <FaListUl className="playlist-icon" />
                <div>
                  <div className="playlist-name">{p.name}</div>
                  {p.description && <div className="playlist-desc">{p.description}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
